import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as auditLogApi from '../api/auditLog';

export function AuditLog() {
  const { selectedOrg } = useOrg();
  const { hasPermission } = usePermissions();
  const canAdmin = hasPermission('admin');
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [filters, setFilters] = useState({
    action: '',
    resourceType: ''
  });

  const loadData = useCallback(async () => {
    if (!selectedOrg || !canAdmin) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const activeFilters = { limit: 100 };
      if (filters.action) activeFilters.action = filters.action;
      if (filters.resourceType) activeFilters.resourceType = filters.resourceType;
      
      const result = await auditLogApi.getAuditLogs(selectedOrg.id, activeFilters);
      setLogs(result.logs);
      setTotal(result.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canAdmin, filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleRowClick = async (logId) => {
    if (!selectedOrg) return;
    try {
      const entry = await auditLogApi.getAuditLogEntry(selectedOrg.id, logId);
      setSelectedEntry(entry);
      setShowDrawer(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const getActionColor = (action) => {
    if (action?.includes('created')) return { bg: '#c6f6d5', color: '#276749' };
    if (action?.includes('updated')) return { bg: '#bee3f8', color: '#2b6cb0' };
    if (action?.includes('deleted')) return { bg: '#fed7d7', color: '#c53030' };
    return { bg: '#e2e8f0', color: '#4a5568' };
  };

  if (!canAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You need admin permissions to view audit logs.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Audit Log"
        subtitle="View all governance and administrative actions"
      />

      <div style={styles.filterBar}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Action</label>
          <select
            value={filters.action}
            onChange={(e) => setFilters(prev => ({ ...prev, action: e.target.value }))}
            style={styles.filterSelect}
            data-testid="action-filter"
          >
            <option value="">All</option>
            <option value="user">User</option>
            <option value="entitlements">Entitlements</option>
            <option value="energyhub">EnergyHub</option>
            <option value="report">Report</option>
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Resource Type</label>
          <select
            value={filters.resourceType}
            onChange={(e) => setFilters(prev => ({ ...prev, resourceType: e.target.value }))}
            style={styles.filterSelect}
            data-testid="resource-filter"
          >
            <option value="">All</option>
            <option value="user">User</option>
            <option value="entitlements">Entitlements</option>
            <option value="integration">Integration</option>
            <option value="energyhub_mapping">EnergyHub Mapping</option>
            <option value="report_schedule">Report Schedule</option>
          </select>
        </div>
        <div style={styles.totalCount}>
          {total} entr{total !== 1 ? 'ies' : 'y'}
        </div>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading audit logs...</div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table} data-testid="audit-table">
            <thead>
              <tr>
                <th style={styles.th}>Time</th>
                <th style={styles.th}>Action</th>
                <th style={styles.th}>Resource Type</th>
                <th style={styles.th}>Resource ID</th>
                <th style={styles.th}>User ID</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => {
                const actionStyle = getActionColor(log.action);
                return (
                  <tr
                    key={log.id}
                    style={styles.tr}
                    onClick={() => handleRowClick(log.id)}
                    data-testid={`log-${log.id}`}
                  >
                    <td style={styles.td}>{new Date(log.timestamp).toLocaleString()}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: actionStyle.bg, color: actionStyle.color }}>
                        {log.action}
                      </span>
                    </td>
                    <td style={styles.td}>{log.resourceType || '-'}</td>
                    <td style={styles.td}>
                      <code style={styles.code}>{log.resourceId || '-'}</code>
                    </td>
                    <td style={styles.td}>
                      <code style={styles.code}>{log.userId || '-'}</code>
                    </td>
                  </tr>
                );
              })}
              {logs.length === 0 && (
                <tr>
                  <td colSpan="5" style={styles.emptyState}>No audit logs found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showDrawer && selectedEntry && (
        <div style={styles.drawerOverlay} onClick={() => setShowDrawer(false)}>
          <div style={styles.drawer} onClick={(e) => e.stopPropagation()} data-testid="audit-drawer">
            <div style={styles.drawerHeader}>
              <h3 style={styles.drawerTitle}>Audit Entry Details</h3>
              <button onClick={() => setShowDrawer(false)} style={styles.closeButton}>×</button>
            </div>
            
            <div style={styles.drawerContent}>
              <div style={styles.drawerSection}>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Entry ID</span>
                  <code style={styles.code}>{selectedEntry.id}</code>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Timestamp</span>
                  <span>{new Date(selectedEntry.timestamp).toLocaleString()}</span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Action</span>
                  <span style={{ ...styles.badge, ...getActionColor(selectedEntry.action) }}>
                    {selectedEntry.action}
                  </span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Resource Type</span>
                  <span>{selectedEntry.resourceType || '-'}</span>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>Resource ID</span>
                  <code style={styles.code}>{selectedEntry.resourceId || '-'}</code>
                </div>
                <div style={styles.drawerRow}>
                  <span style={styles.drawerLabel}>User ID</span>
                  <code style={styles.code}>{selectedEntry.userId || '-'}</code>
                </div>
              </div>

              {selectedEntry.details && Object.keys(selectedEntry.details).length > 0 && (
                <div style={styles.drawerSection}>
                  <h4 style={styles.sectionTitle}>Details</h4>
                  <pre style={styles.details} data-testid="audit-details">
                    {JSON.stringify(selectedEntry.details, null, 2)}
                  </pre>
                </div>
              )}

              {selectedEntry.metadata && Object.keys(selectedEntry.metadata).length > 0 && (
                <div style={styles.drawerSection}>
                  <h4 style={styles.sectionTitle}>Metadata</h4>
                  <pre style={styles.details}>
                    {JSON.stringify(selectedEntry.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  filterBar: { display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'flex-end' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  filterLabel: { fontSize: '0.75rem', fontWeight: '500', color: '#4a5568' },
  filterSelect: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white', minWidth: '150px' },
  totalCount: { marginLeft: 'auto', fontSize: '0.875rem', color: '#718096' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0', cursor: 'pointer', transition: 'background 0.15s' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' },
  badge: { padding: '0.25rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem' },
  code: { background: '#f7fafc', padding: '0.125rem 0.25rem', borderRadius: '2px', fontSize: '0.75rem' },
  emptyState: { padding: '2rem', textAlign: 'center', color: '#718096' },
  drawerOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'flex-end', zIndex: 1000 },
  drawer: { width: '500px', maxWidth: '100%', background: 'white', height: '100%', overflow: 'auto', boxShadow: '-4px 0 6px rgba(0,0,0,0.1)' },
  drawerHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0' },
  drawerTitle: { margin: 0, fontSize: '1.25rem', fontWeight: '600' },
  closeButton: { background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#718096' },
  drawerContent: { padding: '1.5rem' },
  drawerSection: { marginBottom: '1.5rem' },
  drawerRow: { display: 'flex', justifyContent: 'space-between', padding: '0.5rem 0', borderBottom: '1px solid #f7fafc' },
  drawerLabel: { color: '#718096', fontSize: '0.875rem' },
  sectionTitle: { margin: '0 0 0.75rem 0', fontSize: '0.875rem', fontWeight: '600', color: '#2d3748' },
  details: { margin: 0, padding: '1rem', background: '#f7fafc', borderRadius: '8px', fontSize: '0.75rem', overflow: 'auto', whiteSpace: 'pre-wrap' }
};
